// Versão para computador (tela de 1024 px ou mais): menu lateral com todas as seções.
// É o mesmo app: as telas são as mesmas, só o arranjo muda (ver layout.css).
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { api } from '../api.ts';
import { queryClient, useMe } from '../lib/data.ts';
import { initials, tone } from '../lib/format.ts';
import { Icon } from '../ui/Icon.tsx';
import { ALL_ITEMS, GROUPS, MAIN, SETTINGS, useBadges, visible, type MenuItem } from './menu.ts';
import { forgetNav, useNav, type View } from './nav.tsx';
import { openTour } from './tour.tsx';
import { Bell } from '../screens/Engage.tsx';

const WIDE = '(min-width: 1024px)';
const mq = typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(WIDE) : null;
export const useWide = () => useSyncExternalStore((f) => { mq?.addEventListener('change', f); return () => mq?.removeEventListener('change', f); }, () => !!mq?.matches);

// telas internas acendem o item da seção a que pertencem
const SECTION: Partial<Record<View, View>> = {
  cliente: 'clientes', produtos: 'setup', salao: 'setup', horarios: 'setup', folgas: 'setup', regras: 'setup', pagamentos: 'setup', maquininha: 'setup',
  servicos: 'setup', servico: 'setup', equipe: 'setup', parceria: 'setup', acessos: 'setup', perfilAcesso: 'setup', historico: 'setup', meuLink: 'setup', importar: 'setup',
};

// configurações: no computador abrem num painel com a lista de seções ao lado
export const CFG_VIEWS = new Set<View>(['setup', ...(Object.keys(SECTION) as View[]).filter((v) => SECTION[v] === 'setup')]);
// as seções de primeiro nível dentro desse painel também não precisam de "voltar"
export const CFG_TOP = new Set<View>(['salao', 'horarios', 'servicos', 'produtos', 'equipe', 'pagamentos', 'regras', 'meuLink', 'acessos', 'historico']);

// telas que estão no menu lateral: no computador, não precisam do botão de voltar
export const SIDE_VIEWS = new Set<View>([...ALL_ITEMS.map((i) => i.view), 'perfil']);

// menu recolhido (só ícones): para telas de notebook menores. Fica lembrado neste aparelho.
const FOLD_KEY = 'sublime-menu-recolhido';
const readFold = () => { try { return localStorage.getItem(FOLD_KEY) === '1'; } catch { return false; } };

export function SideNav() {
  const nav = useNav();
  const me = useMe().data!;
  const badges = useBadges();
  const [fold, setFold] = useState(readFold);
  const active = SECTION[nav.view] ?? nav.view;
  useEffect(() => { document.documentElement.toggleAttribute('data-menu-recolhido', fold); }, [fold]);
  const toggleFold = () => {
    setFold(!fold);
    try { localStorage.setItem(FOLD_KEY, fold ? '0' : '1'); } catch { /* sem armazenamento */ }
  };
  const item = (i: MenuItem) => {
    const b = i.badge ? badges[i.badge] : null;
    return (
      <button key={i.view} className={'side-item' + (active === i.view ? ' on' : '')} aria-current={active === i.view ? 'page' : undefined} onClick={() => nav.go(i.view)} title={fold ? i.label + (b ? ` (${b.text})` : '') : undefined}>
        <Icon name={i.icon} /><span className="side-label">{i.label}</span>
        {b && <em className={'side-badge' + (b.late ? ' late' : '')}>{b.text}</em>}
      </button>
    );
  };
  return (
    <aside className={'side-nav' + (fold ? ' fold' : '')} aria-label="Menu">
      <div className="side-brand">
        <div className="wordmark-mark" role="img" aria-label="Sublime Nail Design" />
        <span className="side-tools">
          <Bell />
          <button className="side-ic" onClick={toggleFold} aria-pressed={fold} aria-label={fold ? 'Abrir o menu' : 'Recolher o menu'} title={fold ? 'Abrir o menu' : 'Recolher o menu'}><Icon name="panel" /></button>
        </span>
      </div>
      <button className="cta side-new" onClick={() => nav.open({ kind: 'novo' })} aria-label="Novo agendamento"><Icon name="plus" /><span className="side-label">Novo agendamento</span></button>
      <nav className="side-groups" aria-label="Navegação principal">
        <div className="side-group">{visible(MAIN, me).map(item)}</div>
        {GROUPS.map((g) => {
          const shown = visible(g.items, me);
          return shown.length ? (
            <div key={g.title} className="side-group">
              <p className="side-title"><span className="side-label">{g.title}</span></p>
              {shown.map(item)}
            </div>
          ) : null;
        })}
      </nav>
      <div className="side-bottom">
        {visible([SETTINGS], me).map(item)}
        <Account fold={fold} />
      </div>
    </aside>
  );
}

const ROLE: Record<string, string> = { dona: 'Dona', parceira: 'Parceira', recepcao: 'Recepção' };

// conta: um botão só; o menu dele tem perfil, tour e sair
function Account({ fold }: { fold: boolean }) {
  const nav = useNav();
  const me = useMe().data!;
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc); };
  }, [open]);
  const go = (f: () => void) => () => { setOpen(false); f(); };
  return (
    <div className="side-acct" ref={ref}>
      {open && (
        <div className="acct-menu" role="menu" aria-label="Conta">
          <button role="menuitem" onClick={go(() => nav.go('perfil'))}><Icon name="user" />Meu perfil e aparência</button>
          <button role="menuitem" onClick={go(openTour)}><Icon name="info" />Tour pelo app</button>
          <hr />
          <button role="menuitem" className="out" onClick={go(async () => { await api.post('/auth/logout').catch(() => undefined); forgetNav(); queryClient.clear(); window.location.reload(); })}><Icon name="logout" />Sair</button>
        </div>
      )}
      <button className={'side-me' + (open || nav.view === 'perfil' ? ' on' : '')} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(!open)} title={fold ? me.user.name : undefined}>
        <span className={`av t-${tone(me.user.id)}`}>{initials(me.user.name)}</span>
        <span className="side-label"><b>{me.user.name}</b><small>{ROLE[me.role] ?? me.role}</small></span>
        <span className="side-label acct-dots"><Icon name="dotsV" /></span>
      </button>
    </div>
  );
}
